import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ApiError } from "../api/client";
import { isDemo, routerBasename } from "../demo";
import { Spinner } from "../components/tk";
import { DialogHost, ToastProvider } from "../components/tk/overlays";
import { AuthGate, StepUpHost } from "./auth";
import { SessionProvider, useMeQuery, useSession } from "./session";
import { Shell } from "./Shell";

const Results = lazy(() => import("../screens/home/Results"));
const Home = lazy(() => import("../screens/home/Home"));
const Notifications = lazy(() => import("../screens/home/Notifications"));
const Ads = lazy(() => import("../screens/ads/Ads"));
const Approvals = lazy(() => import("../screens/approvals/Approvals"));
const Campaigns = lazy(() => import("../screens/campaigns/Campaigns"));
const Rules = lazy(() => import("../screens/rules/Rules"));
const Agents = lazy(() => import("../screens/agents/Agents"));
const Copilot = lazy(() => import("../screens/copilot/Copilot"));
const Attribution = lazy(() => import("../screens/attribution/Attribution"));
const Channels = lazy(() => import("../screens/channels/Channels"));
const Agency = lazy(() => import("../screens/agency/Agency"));
const Billing = lazy(() => import("../screens/billing/Billing"));
const Settings = lazy(() => import("../screens/settings/Settings"));
const Onboarding = lazy(() => import("../screens/onboarding/Onboarding"));
const ShareView = lazy(() => import("../screens/share/ShareView"));
const Gallery = lazy(() => import("../screens/gallery/Gallery"));
const Admin = lazy(() => import("../screens/admin/Admin"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
    },
  },
});

const Loading = () => <Spinner />;

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter basename={routerBasename()}>
          <Routes>
            <Route
              path="/share/:token"
              element={
                <Suspense fallback={<Loading />}>
                  <ShareView />
                </Suspense>
              }
            />
            <Route
              path="/gallery"
              element={
                <Suspense fallback={<Loading />}>
                  <Gallery />
                </Suspense>
              }
            />
            {/* Internal admin console: its own token, no customer session, never in the demo. */}
            <Route
              path="/admin/*"
              element={
                isDemo() ? (
                  <Navigate to="/" replace />
                ) : (
                  <Suspense fallback={<Loading />}>
                    <Admin />
                  </Suspense>
                )
              }
            />
            <Route path="*" element={<Authed />} />
          </Routes>
        </BrowserRouter>
        <DialogHost />
      </ToastProvider>
    </QueryClientProvider>
  );
}

function Authed() {
  return (
    <AuthGate>
      {(config) => <WithSession config={config} />}
    </AuthGate>
  );
}

function WithSession({ config }: { config: import("../api/types").ConfigResponse }) {
  const me = useMeQuery().data!;
  if (!me.workspaces.length) {
    return (
      <SessionProvider me={me} config={config}>
        <StepUpHost />
        <Suspense fallback={<Loading />}>
          <Onboarding />
        </Suspense>
      </SessionProvider>
    );
  }
  return (
    <SessionProvider me={me} config={config}>
      <StepUpHost />
      <AppRoutes />
    </SessionProvider>
  );
}

function OnboardingGuard({ children }: { children: ReactNode }) {
  const { membership } = useSession();
  const loc = useLocation();
  if (!membership.onboarded && membership.role !== "client_viewer" && !loc.pathname.startsWith("/onboarding") && !loc.pathname.startsWith("/settings") && !loc.pathname.startsWith("/billing")) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <OnboardingGuard>
      <Routes>
        <Route
          path="/onboarding/*"
          element={
            <Suspense fallback={<Loading />}>
              <Onboarding />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Shell>
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/results" element={<Results />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/more" element={<Navigate to="/settings" replace />} />
                  <Route path="/ads/*" element={<Ads />} />
                  <Route path="/approvals/*" element={<Approvals />} />
                  <Route path="/campaigns/*" element={<Campaigns />} />
                  <Route path="/rules/*" element={<Rules />} />
                  <Route path="/agents/*" element={<Agents />} />
                  <Route path="/copilot/*" element={<Copilot />} />
                  <Route path="/attribution/*" element={<Attribution />} />
                  <Route path="/channels/*" element={<Channels />} />
                  <Route path="/agency/*" element={<Agency />} />
                  <Route path="/billing/*" element={<Billing />} />
                  <Route path="/settings/*" element={<Settings />} />
                  <Route path="/auth/*" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Shell>
          }
        />
      </Routes>
    </OnboardingGuard>
  );
}
